<template>
  <div ref="containerRef" class="safe-image-container" :class="imageClass">
    <!-- Thumbnail with blur effect (loads when visible and formats available) -->
    <img 
      v-if="thumbnailUrl && hasFormats"
      :src="thumbnailUrl"
      :alt="finalAltText"
      class="thumbnail-image"
      :class="{ 'fade-out': isMainImageLoaded }"
      @error="handleThumbnailError"
    />
    
    <!-- Main image (loads when visible) -->
    <img 
      v-if="finalImageUrl && isVisible"
      :src="finalImageUrl" 
      :alt="finalAltText"
      ref="mainImageRef"
      :class="[
        hasFormats ? 'main-image' : 'main-image-no-formats',
        hasFormats && isMainImageLoaded ? 'fade-in' : '',
        !hasFormats ? 'fade-in' : ''
      ]"
      @error="handleError"
      @load="handleLoad"
      v-bind="$attrs"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, onMounted, onUnmounted, PropType } from 'vue'
import { useImageErrorHandler, PLACEHOLDER_IMAGES } from '../composables/useImageErrorHandler'

interface ImageFormat {
  url: string;
  width: number;
  height: number;
  size: number;
}

interface ImageFormats {
  thumbnail?: ImageFormat;
  small?: ImageFormat;
  medium?: ImageFormat;
  large?: ImageFormat;
}

interface EnhancedImageData {
  data?: {
    attributes?: {
      url: string;
      alternativeText?: string;
      formats?: ImageFormats;
    }
  }
}

export default defineComponent({
  name: 'SafeImage',
  inheritAttrs: false,
  props: {
    src: {
      type: String as PropType<string | null>,
      default: null
    },
    alt: {
      type: String,
      default: ''
    },
    fallbackSrc: {
      type: String as PropType<string | null>,
      default: null
    },
    placeholder: {
      type: String,
      default: PLACEHOLDER_IMAGES.GENERAL
    },
    placeholderText: {
      type: String,
      default: 'Image not available'
    },
    showPlaceholder: {
      type: Boolean,
      default: true
    },
    imageClass: {
      type: String,
      default: ''
    },
    // Enhanced GraphQL image data support with formats
    imageData: {
      type: Object as PropType<EnhancedImageData>,
      default: null
    },
    // New responsive image props
    size: {
      type: String as PropType<'small' | 'medium' | 'large' | 'auto'>,
      default: 'auto'
    },
    containerWidth: {
      type: Number,
      default: 800
    },
    lazyDelay: {
      type: Number,
      default: 100
    },
    // Lazy loading props
    enableLazyLoading: {
      type: Boolean,
      default: true
    },
    rootMargin: {
      type: String,
      default: '50px'
    },
    // Debug prop
    debug: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { 
      getSafeImageUrl, 
      handleImageError, 
      handleImageLoad,
      getImageUrl,
      getImageAlt 
    } = useImageErrorHandler()
    
    const hasErrored = ref(false)
    const isMainImageLoaded = ref(false)
    const thumbnailErrored = ref(false)
    const isVisible = ref(!props.enableLazyLoading) // If lazy loading disabled, show immediately
    const containerRef = ref<HTMLElement | null>(null)
    const mainImageRef = ref<HTMLElement | null>(null)
    const observer = ref<IntersectionObserver | null>(null)
    const transitionObserver = ref<IntersectionObserver | null>(null)

    // Check if image has responsive formats
    const hasFormats = computed(() => {
      return !!(props.imageData?.data?.attributes?.formats)
    })

    // Get image formats from imageData
    const imageFormats = computed(() => {
      if (!props.imageData?.data?.attributes?.formats) return null
      return props.imageData.data.attributes.formats
    })

    // Get original image URL
    const originalImageUrl = computed(() => {
      if (props.imageData) {
        return getImageUrl(props.imageData)
      }
      return props.src
    })

    // Compute the source URL from either direct src prop or GraphQL imageData
    const sourceUrl = computed(() => {
      // If we have formats, use the responsive selection logic
      if (hasFormats.value && imageFormats.value) {
        const formats = imageFormats.value

        // Auto-select based on container width
        if (props.size === 'auto') {
          if (props.containerWidth <= 500 && formats.small?.url) {
            return formats.small.url
          } else if (props.containerWidth <= 750 && formats.medium?.url) {
            return formats.medium.url
          } else if (formats.large?.url) {
            return formats.large.url
          } else if (formats.medium?.url) {
            return formats.medium.url
          } else if (formats.small?.url) {
            return formats.small.url
          }
        }

        // Use specific size
        if (props.size !== 'auto') {
          const sizeMap = {
            small: formats.small?.url,
            medium: formats.medium?.url,
            large: formats.large?.url
          }

          const preferredUrl = sizeMap[props.size]
          if (preferredUrl) {
            return preferredUrl
          }
        }

        // Fallback to original image
        return originalImageUrl.value
      }

      // Fallback to original logic for backward compatibility
      return originalImageUrl.value
    })

    // Get thumbnail URL (for blur effect)
    const thumbnailUrl = computed(() => {
      if (thumbnailErrored.value || !hasFormats.value) return null
      
      if (imageFormats.value?.thumbnail?.url) {
        return getSafeImageUrl(imageFormats.value.thumbnail.url)
      }
      return null
    })

    // Compute the alt text from either direct alt prop or GraphQL imageData
    const sourceAlt = computed(() => {
      if (props.imageData) {
        return getImageAlt(props.imageData, props.alt)
      }
      return props.alt
    })

    // Determine the final image URL to use
    const finalImageUrl = computed(() => {
      if (hasErrored.value) {
        // If primary source errored, try fallback
        if (props.fallbackSrc) {
          return getSafeImageUrl(props.fallbackSrc)
        }
        // If no fallback or fallback also errored, try placeholder
        if (props.showPlaceholder && props.placeholder) {
          return getSafeImageUrl(props.placeholder)
        }
        return null
      }
      
      // Always use original full-resolution image for the main image
      return getSafeImageUrl(originalImageUrl.value || undefined, props.fallbackSrc || undefined)
    })

    // Final alt text
    const finalAltText = computed(() => {
      return sourceAlt.value || props.placeholderText
    })

    // Simple intersection-based transition (no ScrollTrigger conflicts)
    const setupIntersectionTransition = () => {
      if (hasFormats.value && containerRef.value) {
        const imageTransitionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
                if (props.debug) {
                  console.log('SafeImage: Intersection transition triggered', {
                    intersectionRatio: entry.intersectionRatio.toFixed(2)
                  })
                }
                
                setTimeout(() => {
                  isMainImageLoaded.value = true
                  if (props.debug) {
                    console.log('SafeImage: Main image fade-in activated')
                  }
                }, props.lazyDelay)
                
                // Disconnect after triggering once
                imageTransitionObserver.disconnect()
              }
            })
          },
          {
            threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6], // Multiple thresholds for smooth detection
            rootMargin: '0px'
          }
        )
        
        imageTransitionObserver.observe(containerRef.value)
        
        // Store reference for cleanup  
        transitionObserver.value = imageTransitionObserver
        
        if (props.debug) {
          console.log('SafeImage: Intersection transition setup completed')
        }
      }
    }

    // Handle thumbnail error
    const handleThumbnailError = () => {
      if (thumbnailUrl.value) {
        handleImageError(thumbnailUrl.value)
      }
      thumbnailErrored.value = true
    }

    // Reset error state when source changes
    watch(sourceUrl, () => {
      hasErrored.value = false
      isMainImageLoaded.value = false
    })

    // Reset states when imageData changes
    watch(() => props.imageData, () => {
      hasErrored.value = false
      isMainImageLoaded.value = false
      thumbnailErrored.value = false
    })

    const handleError = () => {
      if (sourceUrl.value) {
        handleImageError(sourceUrl.value)
      }
      hasErrored.value = true
    }

    const handleLoad = () => {
      if (sourceUrl.value) {
        handleImageLoad(sourceUrl.value)
      }
      hasErrored.value = false
      
      if (props.debug) {
        console.log('SafeImage: Main image loaded', { 
          hasFormats: hasFormats.value, 
          url: sourceUrl.value,
          isVisible: isVisible.value
        })
      }
      
      // Setup intersection-based transition instead of immediate transition
      if (hasFormats.value) {
        // Main image is loaded, now setup intersection observer for transition
        requestAnimationFrame(() => {
          setupIntersectionTransition()
        })
      } else {
        // Without formats, show immediately
        isMainImageLoaded.value = true
      }
    }

    // Removed legacy ScrollTrigger function to eliminate conflicts

    // Set up intersection observer for lazy loading with fallback
    onMounted(() => {
      if (props.enableLazyLoading && containerRef.value) {
        // Check if element is already visible on mount (common issue)
        const rect = containerRef.value.getBoundingClientRect()
        const isAlreadyVisible = rect.top < window.innerHeight && rect.bottom > 0
        
        if (isAlreadyVisible) {
          // Element is already visible, set immediately
          isVisible.value = true
          if (props.debug) {
            console.log('SafeImage: Element already visible on mount')
          }
          return
        }
        
        // Use IntersectionObserver for elements not yet visible
        observer.value = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  isVisible.value = true
                  if (props.debug) {
                    console.log('SafeImage: Image became visible via IntersectionObserver')
                  }
                  
                  // Once visible, we can disconnect the observer
                  if (observer.value) {
                    observer.value.disconnect()
                  }
                  
                  // Image is now visible - no need to refresh anything
                }, 50)
              }
            })
          },
          {
            rootMargin: props.rootMargin,
            threshold: 0.05
          }
        )
        
        observer.value.observe(containerRef.value)
        
        // Fallback: Force visibility after 2 seconds if IntersectionObserver fails
        setTimeout(() => {
          if (!isVisible.value) {
            if (props.debug) {
              console.log('SafeImage: Fallback - forcing visibility after timeout')
            }
            isVisible.value = true
          }
        }, 2000)
      }
    })

    // Clean up observers
    onUnmounted(() => {
      if (observer.value) {
        observer.value.disconnect()
      }
      if (transitionObserver.value) {
        transitionObserver.value.disconnect()
      }
    })

    return {
      containerRef,
      mainImageRef,
      finalImageUrl,
      finalAltText,
      handleError,
      handleLoad,
      hasFormats,
      thumbnailUrl,
      isMainImageLoaded,
      handleThumbnailError,
      isVisible
    }
  }
})
</script>

<style scoped>
.safe-image-container {
  position: relative;
  overflow: hidden;
}

.thumbnail-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  /* width: inherit;
  height: inherit; */
  object-fit: cover;
  /* Apply blur effect for low-quality thumbnail */
  @supports (backdrop-filter: blur(20px)) {
    filter: blur(20px);
  }

  transition: opacity 2s ease-out;
  will-change: opacity;
  z-index: 1;
}

.thumbnail-image.fade-out {
  opacity: 0;
}

.main-image {
  position: relative;
  opacity: 0;
  transition: opacity 0.4s ease-out;
  will-change: opacity;
  z-index: 2;
}

.main-image-no-formats {
  position: relative;
  opacity: 1; /* Show immediately for images without formats */
  z-index: 2;
}

.main-image.fade-in {
  opacity: 1;
}
</style>
